import * as THREE from 'three';
import { drawPolylineFromCode } from '../../commands/drawPolylineFromCode';
import { createSolidHatchFromBoundary } from '../../commands/createSolidHatchFromBoundary';
import { generateUniqueId } from '../../utils/generateUniqueId';
import { createTextSprite } from '../../utils/createTextSprite';

export function createMainPanel(scene, mousePos,   isPreview = false, componentId, panelInfo = "Panel",dir = 1) {
  const group = new THREE.Group();
  group.name = "mainPanel";
  const panelID = componentId //|| generateUniqueId('mainPanel');

  const offsetGroup = new THREE.Group();
  offsetGroup.name = "offsetGroup";

  const panelWidth = 15;
  const panelHeight = 50;
  const basePoint = { x: mousePos.x, y: mousePos.y };

  group.position.set(basePoint.x, basePoint.y, 0);
  offsetGroup.position.set(0, 0, 0);
  group.add(offsetGroup);

  const halfW = panelWidth / 2;
  const halfH = panelHeight / 2;

  const providedColor = panelInfo && typeof panelInfo === 'object' && panelInfo.color ? panelInfo.color : null;
  const defaultColor = 0xff0000;
  const symbolColor = providedColor ?? defaultColor;

  // === 1. Polyline ===
  const polyline = drawPolylineFromCode(scene, {
    points: [
      { x: -halfW, y: -halfH },
      { x: -halfW, y: halfH },
      { x: halfW, y: halfH },
      { x: halfW, y: -halfH },
    ],
    color: symbolColor,
    closed: true,
  });

  if (polyline) {
    polyline.userData = { isPanelPart: true, panelID };
    polyline.name = 'mainPanel-outer';
    offsetGroup.add(polyline);
  }

  // === 2. Hatch 1 ===
  const hatch1Element = {
    handle: generateUniqueId(),
    SolidFillFlag: 1,
    HatchPatternName: "SOLID",
  };

  const hatch1Paths = [
    {
      type: "outer",
      groups: [
        {
          edgeType: "Line",
          x1: -halfW,
          y1: halfH,
          x2: halfW,
          y2: halfH
        },
        {
          edgeType: "Line",
          x1: halfW,
          y1: halfH,
          x2: 0,
          y2: 0
        }
      ]
    }
  ];

  const hatch1 = createSolidHatchFromBoundary(hatch1Element, {
    boundaryPaths: hatch1Paths,
    color: symbolColor,
    scene: scene,
    group: offsetGroup,
  });

  if (hatch1) {
    hatch1.name = 'mainPanel-hatch1';
    hatch1.userData = { ...hatch1.userData, isPanelPart: true, panelID };
    offsetGroup.add(hatch1);
  }

  // === 3. Hatch 2 ===
  const hatch2Element = {
    handle: generateUniqueId(),
    SolidFillFlag: 1,
    HatchPatternName: "SOLID",
  };

  const hatch2Paths = [
    {
      type: "outer",
      groups: [
        {
          edgeType: "Line",
          x1: -halfW,
          y1: -halfH,
          x2: halfW,
          y2: -halfH
        },
        {
          edgeType: "Line",
          x1: halfW,
          y1: -halfH,
          x2: 0,
          y2: 0
        }
      ]
    }
  ];

  const hatch2 = createSolidHatchFromBoundary(hatch2Element, {
    boundaryPaths: hatch2Paths,
    color: symbolColor,
    scene: scene,
    group: offsetGroup,
  });

  if (hatch2) {
    hatch2.name = 'mainPanel-hatch2';
    hatch2.userData = { ...hatch2.userData, isPanelPart: true, panelID };
    offsetGroup.add(hatch2);
  }

  // === 4. Kırmızı Ön Kenar Çizgisi ===
  const frontEdgeGeometry = new THREE.BufferGeometry();
  const frontEdgePoints = [
    new THREE.Vector3(halfW, -halfH, 0),
    new THREE.Vector3(halfW, halfH, 0)
  ];
  frontEdgeGeometry.setFromPoints(frontEdgePoints);
  
  const frontEdgeMaterial = new THREE.LineBasicMaterial({ color: symbolColor });
  const frontEdgeLine = new THREE.Line(frontEdgeGeometry, frontEdgeMaterial);
  
  frontEdgeLine.name = 'panel-front-edge';
  frontEdgeLine.userData = {
    isPanelPart: true,
    panelID,
    lineType: 'frontEdge'
  };
  
  offsetGroup.add(frontEdgeLine);

  // === 5. Transparan Tıklama Hatch'i ===
  const clickHatchElement = {
    handle: generateUniqueId(),
    SolidFillFlag: 1,
    HatchPatternName: "SOLID",
  };

  const clickHatchPaths = [
    {
      type: "outer",
      groups: [
        {
          edgeType: "Line",
          x1: -halfW,
          y1: -halfH,
          x2: -halfW,
          y2: halfH
        },
        {
          edgeType: "Line",
          x1: -halfW,
          y1: halfH,
          x2: halfW,
          y2: halfH
        },
        {
          edgeType: "Line",
          x1: halfW,
          y1: halfH,
          x2: halfW,
          y2: -halfH
        }
      ]
    }
  ];

  const clickHatch = createSolidHatchFromBoundary(clickHatchElement, {
    boundaryPaths: clickHatchPaths,
    color: 0x000000,
    opacity: 0.01,
    depthWrite: false,
    transparency: true,
    group: offsetGroup,
  });

  if (clickHatch) {
    clickHatch.name = 'mainPanel-click-hatch';
    clickHatch.userData = {
      ...clickHatch.userData,
      isPanelPart: true,
      panelID,
      originalColor: 0x000000,
      originalOpacity: 0.01,
      originalTransparent: true
    };

    // Manuel olarak transparency ayarlarını uygula
    if (clickHatch.material) {
      clickHatch.material.opacity = 0.01;
      clickHatch.material.transparent = true;
      // colorWrite=false: tıklama alanı asla boyanmaz (raycast çalışır). Transient
      // hover/seçim state'i opacity'yi bozsa bile paneli kaplamaz.
      clickHatch.material.colorWrite = false;
      clickHatch.material.needsUpdate = true;
    }

    offsetGroup.add(clickHatch);
  }

  // === UserData ===
  group.userData = {
    id: panelID,
 
    type: 'mainPanel',
    function: 'electricalPanel',
    name: panelInfo.name ,
    isSelectable: true,
    isPanelPreview: true,
    originalColor: 0xffffff,
    outs: [],
    outSnaps: [],
    basePoint: { ...basePoint },
    height: panelHeight,
    startH: panelHeight / 2,
    endH: panelHeight / 2 - 5,
    details: {
      kullanimAmac: "Mesken",
      aboneTipi: "Mesken",
      kuruluGuc: 5,
      talepFak: "60-40%",
      talepGuc: 5,
      faz: "rst",
      akim: 5,
    },
    salt: {
      kesici: { no: 7, Etiket: "4xB-25A" },
      kolon: { no: 7, Etiket: "4x4mm2 NYM" },
      KAKR: { no: 9, Etiket: "4P 30mA 25A" },
      SayacKesici: { no: 9, Etiket: "4xC-25A" }
    },
    isPreview
  };
 if (panelInfo && panelInfo!=="Panel"
  /*
  && !isPreview */

) {
    const { name = "Panel", installedPower = 0 } = panelInfo;

    const line2 = name;
    const line1 = `${installedPower}W`;

    const titleGroup = new THREE.Group();
    titleGroup.name = "titleGroup";

    // 🔑 dir parametresine göre titleGroup konumlandır
    // dikey yönde (üst/alt)
    titleGroup.position.set(0, (panelHeight / 2 + 16) * dir, 0.1);

    // İlk satır (kurulu güç)
      // İlk satır (kurulu güç)
      // NOTE: Temporarily disabled power text (commented out) to avoid duplicate/small sprites
      /*
      const titleSprite1 = createTextSprite(line1, {
        fontSize: 75,
        color: '#ffffff',
        backgroundColor: 'transparent',
        strokeColor: 'black',
        padding: 4
      });
      titleSprite1.position.set(4*dir, -4*dir, 0);
      titleSprite1.name = "title_" + name;
      titleSprite1.userData = { ...(titleSprite1.userData || {}), role: 'power' };
      */

    // İkinci satır (panel adı)
    const titleSprite2 = createTextSprite(line2, {
      fontSize: 75,
      color: '#ffffff',
      backgroundColor: 'transparent',
      strokeColor: 'black',
      padding: 4
    });
    titleSprite2.position.set(-4*dir, 4*dir, 0);
    titleSprite2.name = "panelTitle2";
    titleSprite2.userData = { ...(titleSprite2.userData || {}), role: 'name' };

  // Title'ları group'a ekle (power text disabled for now)
  // only add the defined name/title sprite
  titleSprite2.userData = { ...(titleSprite2.userData || {}), role: 'name' };
  titleGroup.add(titleSprite2);

    titleGroup.userData = {
      isDraggable: true,
      panelCenter: new THREE.Vector3(0, 0, 0),
      maxDistance: 110, // ✅ Maksimum sürükleme mesafesi 110 birim
      isTitle: true,
      titleType: 'panel',
  // linkedSprites: only keep the name sprite for now (power disabled)
  linkedSprites: [titleSprite2]
    };

    offsetGroup.add(titleGroup);
  }

  if (scene) {
    scene.add(group);
  }

  return group;
}
